import * as THREE from 'three';

export class BlackHole {
    constructor(position = new THREE.Vector3(0, 0, 0), mass = 10) {
        this.position = position.clone();
        this.mass = mass;
        this.schwarzschildRadius = this.calculateSchwarzschildRadius();
        
        this.eventHorizon = null;
        this.photonSphere = null;
        this.group = new THREE.Group();
        
        this.init();
    }

    calculateSchwarzschildRadius() {
        // Simplified: mass determines Schwarzschild radius
        // In real physics: r_s = 2GM/c²
        // For visualization: scale mass to reasonable visual size
        return this.mass * 0.5;
    }

    init() {
        // Event horizon (black sphere with slight glow)
        const eventHorizonGeometry = new THREE.SphereGeometry(this.schwarzschildRadius, 32, 32);
        const eventHorizonMaterial = new THREE.MeshBasicMaterial({
            color: 0x000000,
            side: THREE.DoubleSide
        });
        this.eventHorizon = new THREE.Mesh(eventHorizonGeometry, eventHorizonMaterial);
        this.group.add(this.eventHorizon);
        
        // Add a subtle glow around the event horizon
        const glowGeometry = new THREE.SphereGeometry(this.schwarzschildRadius * 1.02, 32, 32);
        const glowMaterial = new THREE.MeshBasicMaterial({
            color: 0x001122,
            transparent: true,
            opacity: 0.3,
            side: THREE.BackSide
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        this.group.add(glow);
        
        // Photon sphere visualization (at ~1.5 * Schwarzschild radius)
        // This is where light can orbit the black hole
        const photonSphereRadius = this.schwarzschildRadius * 1.5;
        const photonSphereGeometry = new THREE.RingGeometry(
            photonSphereRadius * 0.98,
            photonSphereRadius * 1.02,
            64
        );
        const photonSphereMaterial = new THREE.MeshBasicMaterial({
            color: 0x4488ff,
            transparent: true,
            opacity: 0.2,
            side: THREE.DoubleSide
        });
        this.photonSphere = new THREE.Mesh(photonSphereGeometry, photonSphereMaterial);
        this.photonSphere.rotation.x = Math.PI / 2;
        this.group.add(this.photonSphere);
        
        this.group.position.copy(this.position);
    }

    setMass(mass) {
        this.mass = mass;
        this.schwarzschildRadius = this.calculateSchwarzschildRadius();
        
        // Update event horizon size
        this.eventHorizon.geometry.dispose();
        this.eventHorizon.geometry = new THREE.SphereGeometry(this.schwarzschildRadius, 32, 32);
        
        // Update glow
        const glow = this.group.children.find(child => 
            child !== this.eventHorizon && child !== this.photonSphere
        );
        if (glow) {
            glow.geometry.dispose();
            glow.geometry = new THREE.SphereGeometry(this.schwarzschildRadius * 1.02, 32, 32);
        }
        
        // Update photon sphere
        if (this.photonSphere) {
            const photonSphereRadius = this.schwarzschildRadius * 1.5;
            this.photonSphere.geometry.dispose();
            this.photonSphere.geometry = new THREE.RingGeometry(
                photonSphereRadius * 0.98,
                photonSphereRadius * 1.02,
                64
            );
        }
    }

    getPosition() {
        return this.position.clone();
    }

    getSchwarzschildRadius() {
        return this.schwarzschildRadius;
    }

    getMass() {
        return this.mass;
    }

    getGroup() {
        return this.group;
    }

    update(deltaTime) {
        // Optional: rotate photon sphere
        if (this.photonSphere) {
            this.photonSphere.rotation.z += 0.01 * deltaTime;
        }
    }

    dispose() {
        if (this.eventHorizon) {
            this.eventHorizon.geometry.dispose();
            this.eventHorizon.material.dispose();
        }
        if (this.photonSphere) {
            this.photonSphere.geometry.dispose();
            this.photonSphere.material.dispose();
        }
        this.group.children.forEach(child => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) child.material.dispose();
        });
    }
}
